
import { GeocodingLocationFactory } from '../../geocoding/geocoding-location.factory'
import { Loader } from '@googlemaps/js-api-loader'
import _ from 'lodash'
import Vue, { PropType } from 'vue'
import { ValidationProvider } from 'vee-validate'
import { ZipcodeGetters } from './getters/zipcode-getters'
import { IMapOptions } from './interfaces'
import { QuiteLightMapStyle } from './styles/quite-light'
import {
  DefaultMapCoordinates,
  DefaultMapWithMarkerZoom,
  DefaultMapZoomOnMarkerChange,
  DefaultNewMapZoom,
  googleMapsApiKey,
  ICoordinates,
  CoordinatesGetters,
  LanguageLocale
} from '../../../../../../core/src'
import debounce from 'lodash.debounce'

const AutocompletePlaceChangedEvent = 'place_changed'
const MarkerPositionChangedEvent = 'position_changed'
const coordinatesUpdatedEmitter = 'coordinatesUpdated'
const locationUpdatedEmitter = 'locationUpdated'

interface IData {
  mapCoordinates: ICoordinates
  coordinatesString: string | undefined
}

/**
 * Google maps component. Replacement for the old maps.vue component, removes all Buefy components.
 * This is the google maps package that was used: https://www.npmjs.com/package/@googlemaps/js-api-loader
 * This is the cluster package that was used: https://github.com/googlemaps/js-markerclustererplus
 * If the google.map is not working correctly kindly refer to the links below:
 * https://developers.google.com/maps/documentation/javascript/overview#Inline
 * https://developers.google.com/maps/documentation/javascript/using-typescript
 */
export default Vue.extend({
  components: {
    ValidationProvider
  },
  props: {
    /**
     * Initial central location of the map.
     */
    coordinates: {
      required: true,
      type: Object as PropType<ICoordinates>
    },
    /**
     * Text to use as a label on the map's fields
     * @todo WHY IS THIS HERE IF IT IS NOT USED ANYWHERE??
     */
    label: {
      required: true,
      type: String
    },
    /**
     * Name of the field, as used by the validators.
     */
    fieldName: {
      required: true,
      type: String
    },
    /**
     * Name of the html element in which the map should be displayed. Also used
     * as part of the name of the autocomplete field.
     */
    mapId: {
      required: true,
      type: String
    },
    /**
     * If extra styling such as a frame around the map is needed, styles can be added here.
     */
    css: {
      type: String,
      required: false
    },
    /**
     * Whether the search field is displayed or not.
     */
    searchable: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Whether the map can be edited or not. Some controls will only show up if this is set to true.
     */
    editable: {
      required: false,
      type: Boolean,
      default: false
    },
    language: {
      required: false,
      type: String,
      default: LanguageLocale.USen
    }
  },
  data(): IData {
    const mapCoordinates = {
      lat: this.coordinates.lat ? this.coordinates.lat : DefaultMapCoordinates.lat,
      lng: this.coordinates.lng ? this.coordinates.lng : DefaultMapCoordinates.lng
    } as ICoordinates
    const coordinatesString = CoordinatesGetters.getCoordinatesString(this.coordinates)
    return {
      mapCoordinates,
      coordinatesString
    }
  },
  mounted() {
    const loader = new Loader({
      apiKey: googleMapsApiKey,
      libraries: ['places']
    })
    loader.load().then(this.onMapLoaded)
  },
  methods: {
    /**
     * Generates a new map object.
     * @todo Add types
     */
    createMap(mapId: string, coordinates: ICoordinates, options?: IMapOptions) {
      const zoom = this.determineMapZoom(coordinates)
      const map = new google.maps.Map(document.getElementById(mapId)!, {
        center: {
          // This is redudant but if we don't do this, the google maps API
          // throws out an error
          lat: parseFloat(coordinates.lat!.toString()),
          lng: parseFloat(coordinates.lng!.toString())
        },
        zoom
      })
      map.setOptions({ styles: QuiteLightMapStyle, ...options })
      return map
    },

    /**
     * Determines what the map's coordinates must be. Mostly used to check if a map's
     * coordinates have been already set or not.
     */
    determineCoordinates(coordinates: ICoordinates): ICoordinates {
      const newCoordinates = coordinates || { latitude: DefaultMapCoordinates.lat, longitude: DefaultMapCoordinates.lng }
      return newCoordinates
    },
    /**
     * Determines the map's zoom according to the provided coordinates. If
     * the coordinates are the same as the default coordinates, a default zoom is used for new maps.
     * Otherwise a zoom for a map with an already set location is returned.
     */
    determineMapZoom(coordinates: ICoordinates) {
      let zoom = DefaultMapWithMarkerZoom
      const lngStr = coordinates.lng?.toString()
      const latStr = coordinates.lat?.toString()
      const defaultLngStr = DefaultMapCoordinates.lng.toString()
      const defaultLatStr = DefaultMapCoordinates.lat.toString()
      if (lngStr === defaultLngStr && latStr === defaultLatStr) zoom = DefaultNewMapZoom
      return zoom
    },
    /**
     * Creates a new marker in a given map.
     * @todo Add types
     */
    createMarker(map: google.maps.Map, coordinates: ICoordinates) {
      return new google.maps.Marker({
        position: {
          // This is redudant but if we don't do this, the google maps API
          // throws out an error
          lat: parseFloat(coordinates.lat!.toString()),
          lng: parseFloat(coordinates.lng!.toString())
        },
        map,
        draggable: this.editable
      })
    },
    /**
     * Create an autocomplete field to do google map searches.
     * @todo Add types
     */
    createAutocomplete(mapId: string) {
      const input = document.getElementById(`${mapId}-autocomplete`) as HTMLInputElement
      const options = {
        componentRestrictions: { country: 'jp' },
        origin: DefaultMapCoordinates,
        strictBounds: true
      }
      // eslint-disable-next-line no-undef
      return new google.maps.places.Autocomplete(input, options)
    },
    /**
     * When an autocomplete value is selected, gets the location information and emits
     * update events.
     */
    updateCoordinatesOnAutocomplete(
      autocomplete: google.maps.places.Autocomplete,
      marker: google.maps.Marker,
      map: google.maps.Map
    ): void {
      const place = autocomplete.getPlace()
      if (place && place.geometry) {
        const location = place.geometry.location
        marker.setPosition(location)
        this.mapCoordinates.lat = location!.lat()
        this.mapCoordinates.lng = location!.lng()
        this.coordinatesString = CoordinatesGetters.getCoordinatesString(this.mapCoordinates)
        map.setCenter(location!)
        map.setZoom(DefaultMapZoomOnMarkerChange)
        const addressComponents = place.address_components
        const geocodingLocation = GeocodingLocationFactory.fromGeocodingArrayFactory(addressComponents!)
        geocodingLocation.address = place.formatted_address
        this.$emit(locationUpdatedEmitter, geocodingLocation)
        this.$emit(coordinatesUpdatedEmitter, this.mapCoordinates)
      }
    },
    /**
     * When a marker's location is updated, performs reverse geocoding to get the
     * location's zip code. Then emits update events.
     * @todo Refactor. The geocode call should be done only when the user attempts
     * to save the workplace, to avoid using too many API requests.
     */
    async updateCoordinatesOnMarkerChange(marker: google.maps.Marker): Promise<void> {
      const position = marker.getPosition()!
      this.mapCoordinates.lat = position!.lat()
      this.mapCoordinates.lng = position!.lng()
      this.coordinatesString = CoordinatesGetters.getCoordinatesString(this.mapCoordinates)
      // eslint-disable-next-line no-undef
      const geocoder = new google.maps.Geocoder()
      await geocoder.geocode({ location: position, language: 'ja' }, this.geocoderCallback)
      this.$emit(coordinatesUpdatedEmitter, this.mapCoordinates)
    },
    geocoderCallback(results: google.maps.GeocoderResult[] | null, status: any) {
      const geocodingLocation = ZipcodeGetters.geocode(results, status)
      if (geocodingLocation?.zipCode) this.$emit(locationUpdatedEmitter, geocodingLocation)
    },
    /**
     * Initialization of this component
     */
    onMapLoaded() {
      const coordinates = this.determineCoordinates(this.mapCoordinates)
      const mapOptions = { disableDefaultUI: true } as IMapOptions
      const map = this.createMap(this.mapId, coordinates, mapOptions)
      const marker = this.createMarker(map, coordinates)
      if (this.editable) {
        const autocomplete = this.createAutocomplete(this.mapId)
        autocomplete.addListener(AutocompletePlaceChangedEvent, () => {
          this.updateCoordinatesOnAutocomplete(autocomplete, marker, map)
        })
        marker.addListener(
          MarkerPositionChangedEvent,
          debounce(() => {
            this.updateCoordinatesOnMarkerChange(marker)
          }, 300)
        )
      }
    }
  }
})
